import React, { useState, useEffect } from "react";
import { backupAPI, settingsAPI } from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Upload, Database, CheckCircle2, Loader2, HardDrive, Mail, ChevronDown, Send, Calendar, Clock, Save, Server, FolderOpen, RefreshCw } from "lucide-react";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const CADENCE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

function SettingsPage() {
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [lastBackup, setLastBackup] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);

  
  // Server-side file backup/restore state
  const [serverFiles, setServerFiles] = useState([]);
  const [serverBackupLoading, setServerBackupLoading] = useState(false);
  const [serverRestoreLoading, setServerRestoreLoading] = useState(false);
  const [selectedServerFile, setSelectedServerFile] = useState("");
  const [serverRestoreResult, setServerRestoreResult] = useState(null);
  
  // Email report settings state
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    cadence: "weekly",
    day_of_week: 1,
    day_of_month: 1,
    send_to_osr: true,
    send_to_isr: true
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  


  // Load email settings and server files on mount
  useEffect(() => {
    loadEmailSettings();
    loadServerFiles();
    loadEmailConfig();
  }, []);

  function loadEmailConfig() {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/email-config`)
      .then(res => res.json())
      .then(data => setEmailConfig(data))
      .catch(err => console.error("Failed to load email config:", err));
  }

  function handleSendTestEmail() {
    if (!testEmail) {
      toast.error("Please enter an email address");
      return;
    }
    setSendingTestEmail(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/test-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to_email: testEmail })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          toast.success(data.message);
        } else {
          toast.error(data.detail || "Failed to send test email");
        }
      })
      .catch(err => {
        toast.error("Failed to send test email: " + err.message);
      })
      .finally(() => setSendingTestEmail(false));
  }

  function loadServerFiles() {
    backupAPI.listFiles()
      .then(function(response) {
        if (response.data.success) {
          setServerFiles(response.data.files);
          if (response.data.files.length > 0) {
            setSelectedServerFile(response.data.files[0].path);
          }
        }
      })
      .catch(function(error) {
        console.error("Failed to load server files:", error);
      });
  }

  function handleServerBackup() {
    setServerBackupLoading(true);
    backupAPI.backupToFile()
      .then(function(response) {
        if (response.data.success) {
          toast.success(response.data.message);
          loadServerFiles(); // Refresh file list
        }
      })
      .catch(function(error) {
        toast.error("Failed to create backup: " + (error.response?.data?.detail || error.message));
      })
      .finally(function() {
        setServerBackupLoading(false);
      });
  }

  function handleServerRestore() {
    if (!selectedServerFile) {
      toast.error("Please select a backup file");
      return;
    }
    
    setServerRestoreLoading(true);
    setServerRestoreResult(null);
    
    // Extract just the filename from the path
    const filename = selectedServerFile.split('/').pop();
    
    backupAPI.restoreFromFile(filename)
      .then(function(response) {
        if (response.data.success) {
          setServerRestoreResult(response.data.results);
          toast.success(response.data.message);
        } else {
          toast.error(response.data.message || "Restore failed");
        }
      })
      .catch(function(error) {
        toast.error("Failed to restore: " + (error.response?.data?.detail || error.message));
      })
      .finally(function() {
        setServerRestoreLoading(false);
      });
  }

  function loadEmailSettings() {
    setLoadingSettings(true);
    settingsAPI.getEmailReports()
      .then(function(response) {
        if (response.data.success) {
          setEmailSettings(response.data.data);
        }
      })
      .catch(function(error) {
        console.error("Failed to load email settings:", error);
      })
      .finally(function() {
        setLoadingSettings(false);
      });
  }

  function handleSaveSettings() {
    setSavingSettings(true);
    settingsAPI.updateEmailReports(emailSettings)
      .then(function(response) {
        if (response.data.success) {
          toast.success(response.data.message);
        }
      })
      .catch(function(error) {
        toast.error("Failed to save settings: " + (error.response?.data?.detail || error.message));
      })
      .finally(function() {
        setSavingSettings(false);
      });
  }

  function handleBackup() {
    setBackupLoading(true);
    backupAPI.create()
      .then(function(response) {
        const backupData = response.data;
        if (backupData.success) {
          const blob = new Blob([JSON.stringify(backupData.backup, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
          a.href = url;
          a.download = "airdash-backup-" + timestamp + ".json";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setLastBackup(backupData.summary);
          toast.success("Backup created and downloaded!");
        }
      })
      .catch(function() {
        toast.error("Failed to create backup");
      })
      .finally(function() {
        setBackupLoading(false);
      });
  }

  function handleFileSelect(event) {
    const file = event.target.files[0];
    if (file && file.name.endsWith(".json")) {
      setSelectedFile(file);
      setRestoreResult(null);
    } else {
      toast.error("Please select a JSON file");
    }
  }

  function handleRestore() {
    if (!selectedFile) return;
    setRestoreLoading(true);
    setRestoreResult(null);

    selectedFile.text()
      .then(function(content) {
        const data = JSON.parse(content);
        return backupAPI.restore(data, "replace");
      })
      .then(function(response) {
        setRestoreResult(response.data.results);
        toast.success("Data restored successfully!");
      })
      .catch(function(error) {
        console.error("Restore error:", error);
        const message = error.response?.data?.detail || error.message || "Unknown error";
        toast.error("Failed to restore data: " + message);
      })
      .finally(function() {
        setRestoreLoading(false);
      });
  }

  function handleSendReports(roleFilter) {
    setSendingReports(true);
    setReportResult(null);
    
    reportsAPI.sendWeekly(roleFilter)
      .then(function(response) {
        if (response.data.success) {
          setReportResult(response.data.results);
          const sent = response.data.results.sent.length;
          toast.success(`Sent ${sent} report${sent !== 1 ? 's' : ''} successfully!`);
        }
      })
      .catch(function(error) {
        toast.error("Failed to send reports: " + (error.response?.data?.detail || error.message));
      })
      .finally(function() {
        setSendingReports(false);
      });
  }

  function getScheduleDescription() {
    if (!emailSettings.enabled) return "Auto reports are disabled";
    
    const dayName = DAYS_OF_WEEK.find(d => d.value === emailSettings.day_of_week)?.label || "Monday";
    
    switch (emailSettings.cadence) {
      case "daily":
        return "Reports will be sent every day";
      case "weekly":
        return `Reports will be sent every ${dayName}`;
      case "monthly":
        return `Reports will be sent on day ${emailSettings.day_of_month} of each month`;
      default:
        return "";
    }
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your application data and email reports</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-primary" />
              Backup Data
            </CardTitle>
            <CardDescription>Export all data as JSON</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Database className="h-4 w-4" />
              <span>Projects, Tasks, Task Groups, Resources</span>
            </div>
            <Button onClick={handleBackup} disabled={backupLoading} className="w-full" data-testid="backup-btn">
              {backupLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {backupLoading ? "Creating..." : "Download Backup"}
            </Button>
            {lastBackup && (
              <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Backup Complete
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span>Projects: <Badge variant="secondary">{lastBackup.projects}</Badge></span>
                  <span>Tasks: <Badge variant="secondary">{lastBackup.tasks}</Badge></span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Restore Data
            </CardTitle>
            <CardDescription>Import from backup file</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select Backup File</Label>
              <Input type="file" accept=".json" onChange={handleFileSelect} data-testid="restore-file-input" />
              {selectedFile && (
                <p className="text-sm text-muted-foreground">{selectedFile.name}</p>
              )}
            </div>
            <Button onClick={handleRestore} disabled={!selectedFile || restoreLoading} variant="destructive" className="w-full" data-testid="restore-btn">
              {restoreLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {restoreLoading ? "Restoring..." : "Restore Data"}
            </Button>
            {restoreResult && (
              <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  Restore Complete
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Server-Side File Backup/Restore */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-primary" />
            Server File Backup/Restore
          </CardTitle>
          <CardDescription>Backup and restore directly from files on the server (recommended for VM deployments)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Server Backup */}
            <div className="space-y-3 p-4 border rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <Download className="h-4 w-4" />
                Create Server Backup
              </h4>
              <p className="text-sm text-muted-foreground">
                Save backup to /opt/airdash/backups/
              </p>
              <Button onClick={handleServerBackup} disabled={serverBackupLoading} className="w-full" data-testid="server-backup-btn">
                {serverBackupLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                {serverBackupLoading ? "Creating..." : "Backup to Server"}
              </Button>
            </div>

            {/* Server Restore */}
            <div className="space-y-3 p-4 border rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Restore from Server File
              </h4>
              <div className="flex items-center gap-2">
                <Select value={selectedServerFile} onValueChange={setSelectedServerFile}>
                  <SelectTrigger className="flex-1" data-testid="server-file-select">
                    <SelectValue placeholder="Select backup file" />
                  </SelectTrigger>
                  <SelectContent>
                    {serverFiles.length === 0 ? (
                      <SelectItem value="none" disabled>No backup files found</SelectItem>
                    ) : (
                      serverFiles.map((file) => (
                        <SelectItem key={file.path} value={file.path}>
                          {file.name} ({(file.size / 1024 / 1024).toFixed(1)}MB)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={loadServerFiles} title="Refresh file list">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <Button 
                onClick={handleServerRestore} 
                disabled={serverRestoreLoading || !selectedServerFile} 
                variant="destructive" 
                className="w-full" 
                data-testid="server-restore-btn"
              >
                {serverRestoreLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {serverRestoreLoading ? "Restoring..." : "Restore from File"}
              </Button>
            </div>
          </div>

          {serverRestoreResult && (
            <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                <CheckCircle2 className="h-4 w-4" />
                Server Restore Complete
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <span>Projects: <Badge variant="secondary">{serverRestoreResult.restored?.projects || 0}</Badge></span>
                <span>Tasks: <Badge variant="secondary">{serverRestoreResult.restored?.tasks || 0}</Badge></span>
                <span>Groups: <Badge variant="secondary">{serverRestoreResult.restored?.task_groups || 0}</Badge></span>
                <span>Resources: <Badge variant="secondary">{serverRestoreResult.restored?.resources || 0}</Badge></span>
              </div>
              {serverRestoreResult.source_file && (
                <p className="text-xs text-muted-foreground mt-2">Source: {serverRestoreResult.source_file}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <HardDrive className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">About Backups</h3>
              <p className="text-sm text-muted-foreground">
                Backups include all projects, tasks, task groups, resources, and templates. 
                Store backup files securely and create regular backups before major changes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card></div>
  );
}

export default SettingsPage;
